import React, { useState } from 'react';
import { SignaturePad } from '@/components/mobile/SignaturePad';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function SignatureTest() {
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [capturedSignature, setCapturedSignature] = useState<string | null>(null);

  const handleSignatureCapture = (dataUrl: string) => {
    console.log("✅ Signature captured successfully:", dataUrl.substring(0, 50) + "...");
    setCapturedSignature(dataUrl);
    setShowSignaturePad(false);
  };

  const handleSignatureCancel = () => {
    console.log("❌ Signature cancelled");
    setShowSignaturePad(false);
  };

  const clearSignature = () => {
    setCapturedSignature(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Signature Pad Test</CardTitle>
            <p className="text-sm text-muted-foreground">
              This page tests the restored SignaturePad component in isolation to ensure it works correctly on both mobile and desktop.
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Test Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">Test Instructions</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Tap "Open Signature Pad" button below</li>
                <li>• Try drawing with mouse (desktop) or finger/stylus (mobile)</li>
                <li>• Verify signature appears in real-time</li>
                <li>• Test Clear button functionality</li>
                <li>• Test Cancel and Save buttons</li>
                <li>• Confirm captured signature displays correctly</li>
              </ul>
            </div>

            {/* Signature Display */}
            {capturedSignature && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-2">✅ Captured Signature</h3>
                <div className="bg-white border-2 border-green-300 rounded-lg p-3 mb-3">
                  <img 
                    src={capturedSignature} 
                    alt="Captured Signature" 
                    className="w-full max-h-32 object-contain"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowSignaturePad(true)}
                    variant="outline"
                    size="sm"
                  >
                    Update Signature
                  </Button>
                  <Button
                    onClick={clearSignature}
                    variant="outline"
                    size="sm"
                    className="text-red-600"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}

            {/* Action Button */}
            <div className="text-center">
              <Button
                onClick={() => setShowSignaturePad(true)}
                size="lg"
                className="min-w-48"
              >
                Open Signature Pad
              </Button>
            </div>

            {/* Technical Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">Technical Details</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Component: <code>SignaturePad.tsx</code></li>
                <li>• Library: <code>react-signature-canvas</code></li>
                <li>• Touch Events: Properly configured for mobile</li>
                <li>• Canvas Size: 400x200px (responsive)</li>
                <li>• Output: Base64 PNG data URL</li>
                <li>• Z-Index: 50 (above modal content)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Signature Pad Modal */}
      {showSignaturePad && (
        <SignaturePad
          onSignature={handleSignatureCapture}
          onCancel={handleSignatureCancel}
          title="Test Signature Pad"
          placeholder="Draw your signature to test functionality"
          isSubmitting={false}
        />
      )}
    </div>
  );
}